import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import SEO from "@/components/SEO";
import { PageHeader, Section } from "@/components/Section";
import ReleaseCard from "@/components/ReleaseCard";

const CATEGORIES = [
  { id: "all", label: "All" },
  { id: "latest", label: "Latest" },
  { id: "trending", label: "Trending" },
  { id: "hanuman", label: "Hanuman" },
  { id: "shiva", label: "Shiva" },
  { id: "workout", label: "Workout Bhakti" },
  { id: "meditation", label: "Meditation" },
  { id: "bhajan", label: "Bhajans" },
  { id: "sanskrit", label: "Sanskrit Chants" },
];

const Releases = () => {
  const [params, setParams] = useSearchParams();
  const active = params.get("c") || "all";
  const query = params.get("q") || "";
  const [q, setQ] = useState(query);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (active !== "all") p.category = active === "trending" ? undefined : active;
    if (active === "trending") p.trending = true;
    if (query) p.q = query;
    http.get("/releases", { params: p }).then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, [active, query]);

  const setCat = (id) => {
    const next = new URLSearchParams(params);
    if (id === "all") next.delete("c");
    else next.set("c", id);
    setParams(next);
  };

  const submitQ = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (q) next.set("q", q); else next.delete("q");
    setParams(next);
  };

  const count = useMemo(() => items.length, [items]);

  return (
    <>
      <SEO
        title="Music Library"
        description="Stream every KASHiX release — Sanskrit chants, aggressive bhakti, workout devotionals, meditation music."
      />
      <PageHeader
        eyebrow="Music Library"
        hindi="सम्पूर्ण संग्रह"
        title="The Divine Catalog"
        subtitle="Every release engineered with cinematic depth and devotional intent. Browse by mood, deity, or intensity."
      />
      <Section className="!py-10">
        <div className="flex items-center gap-4 flex-wrap justify-between">
          <div className="flex flex-wrap gap-2 no-scrollbar overflow-x-auto pb-1">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                onClick={() => setCat(c.id)}
                data-testid={`filter-${c.id}`}
                className={`px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.25em] border transition ${
                  active === c.id
                    ? "bg-[#FF9933] text-black border-[#FF9933]"
                    : "border-white/10 text-white/70 hover:border-[#FF9933] hover:text-[#FF9933]"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <form onSubmit={submitQ} className="flex items-center gap-2 glass rounded-full pl-4 pr-2 py-1.5 min-w-[240px]">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search catalog…"
              className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-white/40"
            />
            <button className="rounded-full bg-[#FF9933] text-black px-3 py-1 text-[10px] uppercase tracking-[0.25em] font-semibold hover:bg-[#FFD700]">Go</button>
          </form>
        </div>
        <div className="mt-4 text-[11px] uppercase tracking-[0.3em] text-white/40">
          {loading ? "Loading…" : `${count} track${count === 1 ? "" : "s"}`}
        </div>
      </Section>
      <Section className="!pt-4 !pb-24">
        {items.length === 0 && !loading && (
          <div className="glass rounded-2xl p-10 text-center text-white/60">
            No tracks match. Try a different filter.
          </div>
        )}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {items.map((r, i) => (
            <ReleaseCard key={r.id} release={r} index={i} list={items} />
          ))}
        </div>
      </Section>
    </>
  );
};

export default Releases;
